import {
    AlertCircle,
    Layers3,
    Package,
    ScrollText,
    Sparkles,
    User,
} from 'lucide-react'
import { useEffect } from 'react'

import type {
    DriedTeaPurchaseSummary,
    FreshTeaPurchaseSummary,
    GrowerSummary,
    LineageHop,
    LineageReason,
    ProductLotLineage,
    ScentingLotSummary,
    SortingLotSummary,
} from '@/lib/api/product-lot-api'
import type { LotTraceSource } from '@/lib/lot-trace-tracking'
import { Button } from '@/components/ui/button'
import { useProductLotLineage } from '@/hooks/use-product-lot-query'
import { formatDatePaddedVN } from '@/lib/format'
import { captureLotTraceView } from '@/lib/lot-trace-tracking'
import { m } from '@/lib/paraglide/messages'

type StepKey =
    | 'product'
    | 'scenting'
    | 'sorting'
    | 'tea_purchase'
    | 'fresh_tea_purchase'
    | 'grower'

interface StepRow {
    key: StepKey
    title: string
    icon: React.ComponentType<{ className?: string }>
    available: boolean
    reason?: LineageReason
    dateISO?: string
    body: React.ReactNode
}

interface Props {
    lotId: string
    /** Nơi mở truy xuất, chỉ để đo mức dùng. Mặc định hộp thoại của đơn. */
    source?: LotTraceSource
}

export function LotLineageTimeline({ lotId, source = 'order' }: Props) {
    const { data, isLoading, isError, refetch } = useProductLotLineage(lotId)

    // Một event mỗi lượt tải của lô (tải xong / lỗi). Refetch nền khi đã có
    // dữ liệu và ra cùng kết quả thì không phát lại; bấm "Thử lại" sau lỗi là
    // lượt tải mới (query về pending) nên lỗi tiếp sẽ phát thêm một event.
    const lineage = data?.data
    const traceOutcome = isLoading
        ? null
        : isError || !lineage
          ? 'error'
          : lineage.product_lot.manufactured_date
            ? 'loaded_with_date'
            : 'loaded_without_date'
    useEffect(() => {
        if (traceOutcome === null) return
        if (traceOutcome === 'error') {
            captureLotTraceView({ source, outcome: 'error' })
            return
        }
        captureLotTraceView({
            source,
            outcome: 'loaded',
            hasManufacturedDate: traceOutcome === 'loaded_with_date',
        })
    }, [lotId, source, traceOutcome])

    if (isLoading) {
        return (
            <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                    <div
                        key={i}
                        className="h-16 w-full rounded-md bg-muted animate-pulse"
                    />
                ))}
            </div>
        )
    }

    if (isError || !data?.data) {
        return (
            <div className="flex flex-col items-center gap-2 rounded-md border border-destructive/40 p-6 text-center">
                <AlertCircle className="size-6 text-destructive" />
                <p className="text-sm">{m.order_lot_trace_error()}</p>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void refetch()}
                >
                    {m.order_lot_trace_retry()}
                </Button>
            </div>
        )
    }

    const rows = buildRows(data.data)

    return (
        <div className="relative">
            {/* Vertical connector line — absolute so dots overlay ở đúng vị trí. */}
            <span
                aria-hidden="true"
                className="absolute left-[92px] top-2 bottom-2 w-px bg-border"
            />
            <ol className="space-y-4">
                {rows.map((row) => (
                    <StepRow key={row.key} row={row} />
                ))}
            </ol>
        </div>
    )
}

function StepRow({ row }: { row: StepRow }) {
    const Icon = row.icon
    return (
        <li
            className={`grid grid-cols-[80px_24px_1fr] items-start gap-x-3 ${
                row.available ? '' : 'opacity-60'
            }`}
        >
            <div className="text-xs text-muted-foreground text-right pt-2 tabular-nums">
                {row.dateISO ? formatDatePaddedVN(row.dateISO) : '—'}
            </div>
            <div className="flex justify-center pt-2">
                <span
                    className={`size-3 rounded-full ring-2 ring-background ${
                        row.available
                            ? 'bg-emerald-500'
                            : 'bg-muted-foreground/40'
                    }`}
                />
            </div>
            <div className="pt-1 pb-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                    <Icon
                        className={`size-4 ${
                            row.available
                                ? 'text-emerald-600'
                                : 'text-muted-foreground'
                        }`}
                    />
                    {row.title}
                </div>
                <div className="mt-0.5 text-sm text-muted-foreground">
                    {row.body}
                </div>
            </div>
        </li>
    )
}

function buildRows(lineage: ProductLotLineage): StepRow[] {
    const product = lineage.product_lot
    return [
        // Lô thành phẩm luôn có (chính là lô đang tra). Ngày sản xuất = ngày mẻ
        // của sổ đóng gói sinh ra lô; lô nhập tay có thể không có → "—".
        {
            key: 'product',
            title: m.order_lot_trace_step_product(),
            icon: Package,
            available: true,
            dateISO: product.manufactured_date,
            body: <ProductBody data={product} />,
        },
        // MỌI hop đều multi-source: một phiếu xuất có thể gộp N lô nguồn. Đọc
        // mảng, chọn ngày sớm nhất cho timeline dot, render N item trong body.
        // Với 1 nguồn → identical với UI cũ.
        toRow({
            key: 'scenting',
            title: m.order_lot_trace_step_scenting(),
            icon: Sparkles,
            hop: lineage.scenting_lots,
            renderData: (items: ScentingLotSummary[]) => (
                <MultiBody
                    items={items}
                    render={(d) => <ScentingBody data={d} />}
                />
            ),
            dateGetter: (items: ScentingLotSummary[]) =>
                earliest(items.map((d) => d.created_at)),
        }),
        toRow({
            key: 'sorting',
            title: m.order_lot_trace_step_sorting(),
            icon: Layers3,
            hop: lineage.sorting_lots,
            renderData: (items: SortingLotSummary[]) => (
                <MultiBody
                    items={items}
                    render={(d) => <SortingBody data={d} />}
                />
            ),
            dateGetter: (items: SortingLotSummary[]) =>
                earliest(items.map((d) => d.created_at)),
        }),
        toRow({
            key: 'tea_purchase',
            title: m.order_lot_trace_step_tea_purchase(),
            icon: ScrollText,
            hop: lineage.dried_tea_purchases,
            renderData: (items: DriedTeaPurchaseSummary[]) => (
                <MultiBody
                    items={items}
                    render={(d) => <TeaPurchaseBody data={d} />}
                />
            ),
            dateGetter: (items: DriedTeaPurchaseSummary[]) =>
                earliest(items.map((d) => d.purchase_date)),
        }),
        toRow({
            key: 'fresh_tea_purchase',
            title: m.order_lot_trace_step_fresh_tea_purchase(),
            icon: ScrollText,
            hop: lineage.fresh_tea_purchases,
            renderData: (items: FreshTeaPurchaseSummary[]) => (
                <MultiBody
                    items={items}
                    render={(d) => <FreshTeaPurchaseBody data={d} />}
                    emptyLabel={m.dtp_sources_none()}
                />
            ),
            dateGetter: (items: FreshTeaPurchaseSummary[]) =>
                earliest(items.map((d) => d.purchase_date)),
        }),
        toRow({
            key: 'grower',
            title: m.order_lot_trace_step_grower(),
            icon: User,
            hop: lineage.growers,
            renderData: (items: GrowerSummary[]) => (
                <MultiBody
                    items={items}
                    render={(d) => <GrowerBody data={d} />}
                />
            ),
            dateGetter: () => undefined,
        }),
    ]
}

function earliest(dates: Array<string | undefined>): string | undefined {
    const valid = dates.filter((d): d is string => Boolean(d))
    if (valid.length === 0) return undefined
    return valid.reduce((min, d) => (d < min ? d : min), valid[0])
}

function MultiBody<T>({
    items,
    render,
    emptyLabel,
}: {
    items: T[]
    render: (item: T) => React.ReactNode
    /** Hiện thay vì ẩn khi mảng rỗng (vd "Hộ tự giang" ở hop phiếu tươi). */
    emptyLabel?: string
}) {
    if (items.length === 0) {
        return emptyLabel ? (
            <p className="text-muted-foreground">{emptyLabel}</p>
        ) : null
    }
    if (items.length === 1) {
        return <div>{render(items[0])}</div>
    }
    return (
        <div className="space-y-2">
            {items.map((item, i) => (
                <div key={i} className="border-l-2 border-emerald-500/40 pl-2">
                    {render(item)}
                </div>
            ))}
        </div>
    )
}

function toRow<T>(args: {
    key: StepKey
    title: string
    icon: React.ComponentType<{ className?: string }>
    hop: LineageHop<T>
    renderData: (data: T) => React.ReactNode
    dateGetter: (data: T) => string | undefined
}): StepRow {
    const { key, title, icon, hop, renderData, dateGetter } = args
    if (!hop.available || !hop.data) {
        return {
            key,
            title,
            icon,
            available: false,
            reason: hop.reason,
            body: <GapBody title={title} reason={hop.reason} />,
        }
    }
    return {
        key,
        title,
        icon,
        available: true,
        dateISO: dateGetter(hop.data),
        body: renderData(hop.data),
    }
}

// ------------------- Bodies per hop -------------------

function ProductBody({ data }: { data: ProductLotLineage['product_lot'] }) {
    return (
        <div className="space-y-0.5">
            <p className="font-mono text-foreground">{data.code}</p>
            <p>
                {m.order_lot_trace_field_manufactured_date()}:{' '}
                <span
                    data-testid="lot-trace-manufactured-date"
                    className="text-foreground tabular-nums"
                >
                    {data.manufactured_date
                        ? formatDatePaddedVN(data.manufactured_date)
                        : '—'}
                </span>
            </p>
        </div>
    )
}

function ScentingBody({ data }: { data: ScentingLotSummary }) {
    return (
        <div className="space-y-0.5">
            <p className="font-mono text-foreground">{data.code}</p>
            {data.aroma_material_name && (
                <p>
                    {m.order_lot_trace_field_aroma()}:{' '}
                    <span className="text-foreground">
                        {data.aroma_material_name}
                    </span>
                </p>
            )}
        </div>
    )
}

function SortingBody({ data }: { data: SortingLotSummary }) {
    return (
        <div className="space-y-0.5">
            <p className="font-mono text-foreground">{data.code}</p>
            {data.grade && (
                <p>
                    {m.order_lot_trace_field_grade()}:{' '}
                    <span className="text-foreground">{data.grade}</span>
                </p>
            )}
        </div>
    )
}

function TeaPurchaseBody({ data }: { data: DriedTeaPurchaseSummary }) {
    return (
        <div className="space-y-0.5">
            <p className="font-mono text-foreground">{data.code}</p>
            {data.tea_type_name && (
                <p>
                    {m.order_lot_trace_field_tea_type()}:{' '}
                    <span className="text-foreground">
                        {data.tea_type_name}
                    </span>
                </p>
            )}
            <p>
                {m.order_lot_trace_field_quantity_kg()}:{' '}
                <span className="text-foreground">{data.quantity} kg</span>
            </p>
        </div>
    )
}

function FreshTeaPurchaseBody({ data }: { data: FreshTeaPurchaseSummary }) {
    return (
        <div className="space-y-0.5">
            <p className="font-mono text-foreground">{data.code}</p>
            <p>
                {m.order_lot_trace_field_quantity_kg()}:{' '}
                <span className="text-foreground">{data.quantity} kg</span>
            </p>
        </div>
    )
}

function GrowerBody({ data }: { data: GrowerSummary }) {
    return (
        <div className="space-y-0.5">
            <p className="font-semibold text-foreground">{data.name}</p>
            {data.phone && <p>{data.phone}</p>}
            {data.address && <p className="text-xs">{data.address}</p>}
        </div>
    )
}

function GapBody({ title, reason }: { title: string; reason?: LineageReason }) {
    const detail = reason ? REASON_DETAIL[reason]?.() : undefined
    return (
        <div>
            <p>{m.order_lot_trace_missing({ step: title })}</p>
            {detail && <p className="mt-0.5 text-xs">{detail}</p>}
        </div>
    )
}

const REASON_DETAIL: Partial<Record<LineageReason, () => string>> = {
    packaging_pnk_reason_not_from_process: () =>
        m.order_lot_trace_reason_packaging_pnk(),
    packaging_sources_missing: () => m.order_lot_trace_reason_bridge_missing(),
    scenting_process_not_found: () => m.order_lot_trace_reason_bridge_missing(),
    scenting_sources_missing: () => m.order_lot_trace_reason_bridge_missing(),
    sorting_sources_missing: () => m.order_lot_trace_reason_bridge_missing(),
    grower_missing: () => m.order_lot_trace_reason_grower_missing(),
}
